import AsyncStorage from '@react-native-async-storage/async-storage';
import { CALC_ROUTE_SET, CalcCategory, findCalc } from './calcRegistry';

export type HistoryResultLine = {
  label: string;
  value: string;
  unit?: string;
};

export type HistoryEntry = {
  /** Unix ms */
  ts: number;
  route: string;
  title: string;
  category: CalcCategory;
  result: HistoryResultLine[];
};

const KEY = 'tabai.history.v1';
const MAX = 200;

export async function loadHistory(): Promise<HistoryEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e) =>
        e &&
        typeof e.ts === 'number' &&
        typeof e.route === 'string' &&
        CALC_ROUTE_SET.has(e.route) &&
        Array.isArray(e.result),
    );
  } catch (e) {
    console.warn('[history] loadHistory failed:', e);
    return [];
  }
}

async function save(entries: HistoryEntry[]): Promise<void> {
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(entries.slice(0, MAX)));
  } catch (e) {
    console.warn('[history] save failed:', e);
  }
}

export async function appendHistory(entry: {
  route: string;
  result: HistoryResultLine[];
}): Promise<void> {
  const calc = findCalc(entry.route);
  if (!calc) return;
  if (!entry.result || entry.result.length === 0) return;
  const existing = await loadHistory();
  const next: HistoryEntry[] = [
    {
      ts: Date.now(),
      route: calc.route,
      title: calc.title,
      category: calc.category,
      result: entry.result.map((r) => ({
        label: r.label,
        value: r.value,
        unit: r.unit,
      })),
    },
    ...existing,
  ].slice(0, MAX);
  await save(next);
}

export async function clearHistory(): Promise<void> {
  try {
    await AsyncStorage.removeItem(KEY);
  } catch (e) {
    console.warn('[history] clearHistory failed:', e);
  }
}

/**
 * Cheap signature of a result set — used to avoid duplicate log entries
 * when CalcShell re-renders with the same result.
 */
export function resultSignature(lines: HistoryResultLine[] | null | undefined): string {
  if (!lines || lines.length === 0) return '';
  return lines.map((l) => `${l.label}=${l.value}${l.unit ?? ''}`).join('|');
}
